import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Sparkles, Zap, DollarSign, Shield, CheckCircle2, ArrowRight,
  Upload, Users, BarChart3, Headphones, Loader2, Globe, Building2,
  Wand2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BENEFITS = [
  {
    icon: DollarSign,
    title: '0% platform fees for 6 months',
    body: 'Approved for the first 100 creators. Keep 100% of your revenue for 6 months. After that, a flat 5% take rate — no tiers, no surprises.',
  },
  {
    icon: Zap,
    title: 'Built-in distribution',
    body: 'Your listing is surfaced to our curated buyer base + pushed through the automated SEO engine publishing 3x per week.',
  },
  {
    icon: Shield,
    title: 'Refunds & disputes — handled',
    body: 'We run checkout, refunds, chargebacks, and tax. You focus on the product, we handle the buyer-facing operations end-to-end.',
  },
  {
    icon: BarChart3,
    title: 'Real analytics',
    body: 'Live link clicks, conversion by source, buyer cohorts, refund rate. Know exactly what\'s selling and why.',
  },
  {
    icon: Users,
    title: 'Affiliate-ready',
    body: 'Launch your own affiliate program in two clicks. 30% default commission, tracked with live dashboards.',
  },
  {
    icon: Headphones,
    title: 'AI Listing Optimizer',
    body: 'Every approved listing runs through our LLM-powered optimizer the moment you submit. You get a one-page report inside ~30 seconds: SEO score with explanations, missing trust signals, pricing benchmark vs. comparable AI tools, 3 alternative titles & descriptions, and a 5-item launch-week checklist. Apply with one click — no calls, no async ping-pong.',
  },
];

const STEPS = [
  { n: '01', title: 'Apply below', body: 'Takes about 60 seconds. Tell us what you\'re selling and where we can see your work.' },
  { n: '02', title: 'We review', body: 'Typically within 72 hours. Approved sellers get an invite email with a password-set link.' },
  { n: '03', title: 'List and launch', body: 'Upload your product, set pricing, and go live.' },
];

export default function Submit() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    verification_link: '',
    product_url: '',
    description: '',
    phone: '',
    ownership_declared: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  // AI Listing Optimizer state — populated after a successful submit. We
  // request the report on a separate call so the submit response itself
  // stays fast; the success page shows a loader while the LLM is working.
  const [requestId, setRequestId] = useState(null);
  const [optimizerToken, setOptimizerToken] = useState(null);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const fetchOptimizationReport = async (rid, token) => {
    if (!rid) return;
    setReportLoading(true);
    setReportError(null);
    try {
      // Pass the one-shot token as a query param. Without it the endpoint
      // returns 403, so guessing a request_id no longer burns LLM budget.
      const url = `${API_URL}/api/vip-creator-requests/${rid}/optimization-report`
        + (token ? `?token=${encodeURIComponent(token)}` : '');
      const res = await axios.post(url);
      setReport(res.data?.report || null);
    } catch (err) {
      setReportError(err.response?.data?.detail || 'Couldn\'t generate the report right now. Try again or wait — we\'ll email you the report when we review your application.');
    } finally {
      setReportLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.ownership_declared) {
      toast.error('Please confirm you own the rights to the products you want to list.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/vip-creator-request`, form);
      const rid = res.data?.request_id;
      const tok = res.data?.optimizer_token;
      setRequestId(rid);
      setOptimizerToken(tok);
      setDone(true);
      toast.success('Application received! Generating your AI Listing Optimization report…');
      // Kick off the optimizer in the background — we only get a useful
      // report when there's a product URL + a real description, so skip
      // gracefully when those weren't provided.
      if (rid && form.product_url && form.description.length >= 30) {
        fetchOptimizationReport(rid, tok);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    const reportReady = report && report.status === 'ok';
    return (
      <div className="min-h-screen bg-black text-white p-6">
        <div className="max-w-3xl w-full mx-auto bg-neutral-900/60 border border-white/10 rounded-2xl p-8 md:p-12">
          <div className="text-center mb-8">
            <CheckCircle2 className="w-14 h-14 text-[#00F0FF] mx-auto mb-5" />
            <h1 className="text-3xl md:text-4xl font-bold mb-3">
              You&apos;re in the queue
            </h1>
            <p className="text-neutral-400">
              We review every application by hand. You&apos;ll hear back at <span className="text-white font-medium">{form.email}</span> within 72 hours.
            </p>
          </div>

          {/* AI Listing Optimization report — replaces the old "hands-on
              onboarding" promise. Every applicant with a product URL + real
              description gets concrete, actionable feedback in ~30 seconds. */}
          {(reportLoading || reportReady || reportError) && (
            <div className="border-t border-white/10 pt-8 mt-8" data-testid="submit-optimizer-report">
              <div className="flex items-center gap-2 mb-2">
                <Wand2 className="w-5 h-5 text-[#00F0FF]" />
                <h2 className="text-xl font-bold">Your AI Listing Optimization Report</h2>
              </div>
              <p className="text-sm text-neutral-500 mb-6">
                Free with every Founding Creator application. We analyze your product URL and description to surface the highest-impact wins before launch.
              </p>

              {reportLoading && (
                <div className="flex items-center gap-3 p-6 bg-neutral-900/40 border border-white/10 rounded-xl" data-testid="optimizer-loading">
                  <Loader2 className="w-5 h-5 text-[#00F0FF] animate-spin" />
                  <span className="text-sm text-neutral-300">Analyzing your listing… typically 20-40 seconds.</span>
                </div>
              )}

              {reportError && (
                <div className="p-5 bg-red-500/10 border border-red-500/30 rounded-xl text-sm text-red-300" data-testid="optimizer-error">
                  {reportError}
                </div>
              )}

              {reportReady && (
                <div className="space-y-6">
                  {/* SEO Score */}
                  <section className="bg-neutral-900/40 border border-white/10 rounded-xl p-5" data-testid="optimizer-seo">
                    <div className="flex items-baseline justify-between gap-4 mb-3">
                      <h3 className="text-sm font-semibold uppercase tracking-wider text-[#00F0FF]">SEO Score</h3>
                      <p className="text-3xl font-black text-white tabular-nums">{report.seo_score}<span className="text-base text-neutral-500">/100</span></p>
                    </div>
                    <ul className="space-y-1.5 text-sm text-neutral-300">
                      {(report.seo_reasons || []).map((r, i) => (
                        <li key={i} className="flex gap-2"><span className="text-[#00F0FF] flex-shrink-0">•</span><span>{r}</span></li>
                      ))}
                    </ul>
                  </section>

                  {/* Trust Signals */}
                  <section className="bg-neutral-900/40 border border-white/10 rounded-xl p-5" data-testid="optimizer-trust">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-[#00F0FF] mb-3">Trust Signals</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-green-400 font-semibold uppercase mb-2">Present</p>
                        <ul className="space-y-1.5 text-sm text-neutral-300">
                          {(report.trust_signals?.present || []).map((s, i) => (
                            <li key={i} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" /><span>{s}</span></li>
                          ))}
                          {(report.trust_signals?.present || []).length === 0 && <li className="text-neutral-500 italic">None detected.</li>}
                        </ul>
                      </div>
                      <div>
                        <p className="text-xs text-yellow-400 font-semibold uppercase mb-2">Missing — Add These</p>
                        <ul className="space-y-1.5 text-sm text-neutral-300">
                          {(report.trust_signals?.missing || []).map((s, i) => (
                            <li key={i} className="flex gap-2"><span className="text-yellow-400 flex-shrink-0">+</span><span>{s}</span></li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </section>

                  {/* Pricing Benchmark */}
                  {report.pricing_benchmark && (
                    <section className="bg-neutral-900/40 border border-white/10 rounded-xl p-5" data-testid="optimizer-pricing">
                      <h3 className="text-sm font-semibold uppercase tracking-wider text-[#00F0FF] mb-3">Pricing Benchmark</h3>
                      <p className="text-2xl font-bold text-white mb-2">{report.pricing_benchmark.suggested_range_usd}</p>
                      <p className="text-sm text-neutral-300 leading-relaxed">{report.pricing_benchmark.reasoning}</p>
                    </section>
                  )}

                  {/* Title Suggestions */}
                  {Array.isArray(report.title_suggestions) && report.title_suggestions.length > 0 && (
                    <section className="bg-neutral-900/40 border border-white/10 rounded-xl p-5" data-testid="optimizer-titles">
                      <h3 className="text-sm font-semibold uppercase tracking-wider text-[#00F0FF] mb-3">Suggested Titles</h3>
                      <ol className="space-y-2 text-sm text-neutral-200">
                        {report.title_suggestions.map((t, i) => (
                          <li key={i} className="flex gap-3 items-start">
                            <span className="text-neutral-500 font-mono text-xs mt-0.5">{i + 1}.</span>
                            <span>{t}</span>
                          </li>
                        ))}
                      </ol>
                    </section>
                  )}

                  {/* Description Suggestions */}
                  {Array.isArray(report.description_suggestions) && report.description_suggestions.length > 0 && (
                    <section className="bg-neutral-900/40 border border-white/10 rounded-xl p-5" data-testid="optimizer-descriptions">
                      <h3 className="text-sm font-semibold uppercase tracking-wider text-[#00F0FF] mb-3">Description Rewrites</h3>
                      <ol className="space-y-3 text-sm text-neutral-200">
                        {report.description_suggestions.map((d, i) => (
                          <li key={i} className="border-l-2 border-[#00F0FF]/40 pl-3">{d}</li>
                        ))}
                      </ol>
                    </section>
                  )}

                  {/* Launch Checklist */}
                  {Array.isArray(report.launch_checklist) && report.launch_checklist.length > 0 && (
                    <section className="bg-neutral-900/40 border border-white/10 rounded-xl p-5" data-testid="optimizer-checklist">
                      <h3 className="text-sm font-semibold uppercase tracking-wider text-[#00F0FF] mb-3">Launch-Week Checklist</h3>
                      <ul className="space-y-2 text-sm text-neutral-200">
                        {report.launch_checklist.map((c, i) => (
                          <li key={i} className="flex gap-2"><span className="text-[#00F0FF] font-bold flex-shrink-0">{String(i + 1).padStart(2, '0')}</span><span>{c}</span></li>
                        ))}
                      </ul>
                    </section>
                  )}

                  <p className="text-xs text-neutral-500 text-center">
                    Save this page — we&apos;ll also email a copy to <span className="text-neutral-400">{form.email}</span> when we review your application.
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="text-center mt-8">
            <Link to="/">
              <Button className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 font-semibold" data-testid="submit-done-home">
                Back to marketplace <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="submit-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 pointer-events-none opacity-40"
             style={{ backgroundImage: 'radial-gradient(circle at 20% 10%, rgba(0,240,255,0.15), transparent 40%), radial-gradient(circle at 80% 70%, rgba(255,215,0,0.08), transparent 45%)' }}></div>
        <div className="max-w-5xl mx-auto px-5 md:px-8 py-20 md:py-28 relative">
          <div className="flex items-center gap-2 text-[#00F0FF] text-sm uppercase font-bold mb-4">
            <Sparkles className="w-4 h-4" /> Founding creator program
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-5">
            Sell your AI tool on the marketplace buyers actually trust.
          </h1>
          <p className="text-lg md:text-xl text-neutral-400 max-w-2xl mb-3">
            theailedger.ai is the curated marketplace for ready-to-deploy AI —
            prompt packs, agents, automations, templates and done-for-you services.
            Apply as one of our first 100 founding creators and keep 100% of your revenue for 6 months.
          </p>
          {/* Founding Creator fee disclaimer — moved here from the Home.js
              hero per founder request: the homepage now stays focused on
              browsing, and the offer terms are surfaced the moment a user
              clicks Apply/Sell and lands on the creator signup page. */}
          <p
            className="text-sm md:text-base text-[#00F0FF]/90 max-w-2xl mb-8 font-semibold"
            data-testid="submit-fee-disclaimer"
          >
            First 100 creators get 0% for 6 months, then flat 5% — no subscription
          </p>
          <div className="flex flex-wrap gap-3">
            <a href="#apply">
              <Button className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 font-semibold text-base px-6 py-6" data-testid="submit-hero-cta">
                Apply to sell <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <Link to="/partners/platforms">
              <Button
                variant="outline"
                className="border-[#FFB400]/40 text-[#FFB400] hover:bg-[#FFB400]/5 text-base px-6 py-6"
                data-testid="submit-platform-cta"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                All-in-one platform? $99/mo featured slot →
              </Button>
            </Link>
            <Link to="/partners/apply">
              <Button
                variant="outline"
                className="border-white/20 text-white hover:bg-white/5 text-base px-6 py-6"
                data-testid="submit-partner-cta"
              >
                <Building2 className="w-4 h-4 mr-2" />
                Enterprise / verified partner →
              </Button>
            </Link>
          </div>

          <div className="mt-5 p-4 rounded-xl bg-white/[0.03] border border-[#00F0FF]/30 max-w-2xl">
            <p className="text-sm text-neutral-300 mb-2">
              <span className="font-bold text-[#00F0FF]">On the fence?</span> Paste your URL and
              we'll show you your SEO score, conservative monthly earnings range, and the top fixes
              to make — before you apply.
            </p>
            <Link
              to="/optimizer"
              data-testid="submit-optimizer-link"
              className="inline-flex items-center gap-1 text-sm font-semibold text-[#00F0FF] hover:underline"
            >
              Get my free audit + earnings estimate →
            </Link>
          </div>

          <div className="mt-3 p-4 rounded-xl bg-white/[0.03] border border-white/10 max-w-2xl">
            <p className="text-sm text-neutral-300 mb-3">
              <span className="font-bold text-white">Are you a founder evaluating partnerships?</span>{' '}
              We offer five revenue lanes (Founding Creator, Standard Creator, Featured Platform
              Partner, AI Build Partner, Affiliate Partner). Pick the path that fits — both
              applications go to the same admin queue:
            </p>
            <div className="flex flex-wrap gap-2 mb-2">
              <Link
                to="/partners/apply?type=affiliate"
                data-testid="submit-apply-affiliate-link"
                className="inline-flex items-center gap-1 text-sm font-semibold px-3 py-2 rounded-lg bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90"
              >
                Apply as Affiliate Partner →
              </Link>
              <Link
                to="/partners/apply"
                data-testid="submit-apply-business-link"
                className="inline-flex items-center gap-1 text-sm font-semibold px-3 py-2 rounded-lg bg-white/[0.06] border border-white/10 text-white hover:bg-white/[0.1]"
              >
                Apply as Subscription / Business Partner →
              </Link>
            </div>
            <a
              href="mailto:partnerships@theailedger.ai?subject=Partnership%20inquiry"
              data-testid="submit-partnerships-link"
              className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-[#00F0FF]"
            >
              Not sure which fits? Email partnerships@theailedger.ai →
            </a>
          </div>

          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-neutral-500">
            <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#00F0FF]" /> Hand-reviewed approvals</div>
            <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#00F0FF]" /> 72-hour response time</div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-20">
        <h2 className="text-base md:text-lg text-[#00F0FF] uppercase font-bold mb-2">Why creators choose us</h2>
        <h3 className="text-3xl md:text-4xl font-bold mb-10 max-w-2xl">
          Everything you need to monetise your AI work — in one place.
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {BENEFITS.map((b) => (
            <div key={b.title} className="bg-neutral-900/40 border border-white/10 rounded-xl p-6 hover:border-[#00F0FF]/40 transition">
              <b.icon className="w-6 h-6 text-[#00F0FF] mb-4" />
              <div className="font-bold text-lg mb-2">{b.title}</div>
              <div className="text-sm text-neutral-400">{b.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* What can I sell? — buyer-facing benefits only, no internal architecture leaked */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 pt-4 pb-10">
        <h2 className="text-base md:text-lg text-[#00F0FF] uppercase font-bold mb-2">What can I sell?</h2>
        <h3 className="text-3xl md:text-4xl font-bold mb-3">If it's an AI product, it fits here.</h3>
        <p className="text-neutral-400 max-w-2xl mb-8 text-sm md:text-base">
          theailedger handles checkout, tax, refunds, and payouts on your behalf.
          You ship great work — we run the storefront, the billing, and the buyer experience.
        </p>
        <div className="grid md:grid-cols-3 gap-5" data-testid="sell-patterns">
          <div className="bg-neutral-900/40 border border-white/10 rounded-xl p-6 hover:border-[#00F0FF]/40 transition">
            <div className="text-xs uppercase tracking-wide text-[#00F0FF] font-bold mb-3">One-time purchases</div>
            <div className="font-bold text-lg mb-2">Files, packs, lifetime deals</div>
            <ul className="text-sm text-neutral-400 space-y-1.5 mt-3">
              <li>• Prompt packs, agent templates, scripts</li>
              <li>• Notion templates, Figma kits, datasets</li>
              <li>• Lifetime SaaS deals ($199 once)</li>
              <li>• Pre-paid annual seats</li>
            </ul>
          </div>

          <div className="bg-neutral-900/40 border border-white/10 rounded-xl p-6 hover:border-[#00F0FF]/40 transition">
            <div className="text-xs uppercase tracking-wide text-[#00F0FF] font-bold mb-3">Recurring</div>
            <div className="font-bold text-lg mb-2">Monthly or yearly subscriptions</div>
            <ul className="text-sm text-neutral-400 space-y-1.5 mt-3">
              <li>• $X / month or $X / year billing</li>
              <li>• Free-trial periods supported</li>
              <li>• Automatic billing &amp; retries handled for you</li>
              <li>• Buyers self-manage their subscription</li>
            </ul>
          </div>

          <div className="bg-neutral-900/40 border border-white/10 rounded-xl p-6 hover:border-[#00F0FF]/40 transition">
            <div className="text-xs uppercase tracking-wide text-[#00F0FF] font-bold mb-3">Credit packs &amp; seats</div>
            <div className="font-bold text-lg mb-2">Usage bundles and team plans</div>
            <ul className="text-sm text-neutral-400 space-y-1.5 mt-3">
              <li>• &ldquo;$50 = 1,000 API credits&rdquo;</li>
              <li>• &ldquo;10 seats for $199&rdquo;</li>
              <li>• Refill-as-needed model</li>
              <li>• One-time charge per refill</li>
            </ul>
          </div>
        </div>
        <p className="text-xs text-neutral-500 mt-6 max-w-3xl">
          <span className="text-neutral-300 font-semibold">Already running your own billing?</span>{' '}
          Most SaaS creators on theailedger keep their normal recurring page running on their own site
          and list a marketplace-only SKU here — typically a lifetime deal or prepaid annual.
          Same model as AppSumo / Pitchground / StackSocial.
        </p>
      </section>

      {/* How it works */}
      <section className="border-y border-white/10 bg-neutral-950/50">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-20">
          <h2 className="text-base md:text-lg text-[#00F0FF] uppercase font-bold mb-2">How it works</h2>
          <h3 className="text-3xl md:text-4xl font-bold mb-10">Three steps. No games.</h3>
          <div className="grid md:grid-cols-3 gap-5">
            {STEPS.map((s) => (
              <div key={s.n} className="bg-black/60 border border-white/10 rounded-xl p-6">
                <div className="text-5xl font-bold text-[#00F0FF]/30 mb-3">{s.n}</div>
                <div className="font-bold text-lg mb-2">{s.title}</div>
                <div className="text-sm text-neutral-400">{s.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Apply form */}
      <section id="apply" className="max-w-3xl mx-auto px-5 md:px-8 py-20">
        <h2 className="text-base md:text-lg text-[#00F0FF] uppercase font-bold mb-2">Apply to sell</h2>
        <h3 className="text-3xl md:text-4xl font-bold mb-8">Get approved in 72 hours.</h3>

        <form onSubmit={submit} className="space-y-5 bg-neutral-900/40 border border-white/10 rounded-2xl p-6 md:p-8" data-testid="submit-apply-form">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-sm">Your name <span className="text-[#00F0FF]">*</span></Label>
              <Input
                required
                value={form.name}
                onChange={update('name')}
                className="bg-black/40 border-white/10"
                placeholder="Jane Doe"
                data-testid="submit-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm">Email <span className="text-[#00F0FF]">*</span></Label>
              <Input
                required
                type="email"
                value={form.email}
                onChange={update('email')}
                className="bg-black/40 border-white/10"
                placeholder="you@yourdomain.com"
                data-testid="submit-email"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm flex items-center gap-2">
              <Globe className="w-3.5 h-3.5 text-neutral-400" />
              A link where we can see your work <span className="text-[#00F0FF]">*</span>
            </Label>
            <Input
              required
              type="url"
              value={form.verification_link}
              onChange={update('verification_link')}
              className="bg-black/40 border-white/10"
              placeholder="https://linkedin.com/in/you  or  https://x.com/you  (a social media profile)"
              data-testid="submit-link"
            />
            <p className="text-xs text-neutral-500">
              Anything that proves you&rsquo;re a real person:&nbsp;
              <span className="text-neutral-400">portfolio site, GitHub repo, LinkedIn, X, or Gumroad &mdash; <strong className="text-[#00F0FF]">your social media profile</strong>, not your profile on this platform.</span>
              &nbsp;NOT the product itself &mdash; that goes in the next field.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm flex items-center gap-2">
              <Globe className="w-3.5 h-3.5 text-neutral-400" />
              Live product URL <span className="text-neutral-500 font-normal text-xs">(optional &mdash; if your product is already online)</span>
            </Label>
            <Input
              type="url"
              value={form.product_url}
              onChange={update('product_url')}
              className="bg-black/40 border-white/10"
              placeholder="https://yourapp.com or https://gumroad.com/l/your-pack"
              data-testid="submit-product-url"
            />
            <p className="text-xs text-neutral-500">
              Direct link to the AI product, prompt pack, GPT, or template you want to sell.
              Leave blank if it&rsquo;s not live anywhere yet &mdash; we still review.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm">What will you sell?</Label>
            <Textarea
              value={form.description}
              onChange={update('description')}
              className="bg-black/40 border-white/10 min-h-[100px]"
              placeholder="Optional but helpful. Tell us about the AI product, prompt pack, agent or template you want to launch."
              data-testid="submit-description"
            />
            <p className="text-xs text-neutral-500" data-testid="submit-description-helper">
              {form.description.length < 30 ? (
                <>
                  Tip: write at least <span className="text-[#00F0FF]">30 characters</span> ({form.description.length}/30) so we can run your <span className="text-white font-medium">AI Listing Optimization report</span> on submit. Short blurbs skip the report.
                </>
              ) : (
                <>
                  <span className="text-[#00F0FF]">✓</span> Long enough — you&apos;ll get an AI Listing Optimization report on the next screen.
                </>
              )}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm">
              Mobile / phone <span className="text-neutral-500 font-normal text-xs">(optional &mdash; for time-sensitive launch updates only)</span>
            </Label>
            <Input
              type="tel"
              value={form.phone}
              onChange={update('phone')}
              className="bg-black/40 border-white/10"
              placeholder="+1 555 123 4567"
              data-testid="submit-phone"
            />
            <p className="text-xs text-neutral-500">
              We&rsquo;ll only text for things that actually matter (last-call Founding spots, order-volume spikes, surprise feature drops). You can remove it from your dashboard any time.
            </p>
          </div>

          <label className="flex items-start gap-3 text-sm text-neutral-300">
            <input
              type="checkbox"
              checked={form.ownership_declared}
              onChange={(e) => setForm((f) => ({ ...f, ownership_declared: e.target.checked }))}
              className="mt-1 w-4 h-4 accent-[#00F0FF]"
              required
              data-testid="submit-ownership"
            />
            <span>
              I own or have licensing rights to everything I&apos;ll list, and I accept the&nbsp;
              <Link to="/terms" target="_blank" className="text-[#00F0FF] hover:underline">Creator Terms</Link>&nbsp;
              and&nbsp;
              <Link to="/refund-policy" target="_blank" className="text-[#00F0FF] hover:underline">Refund Policy</Link>.
            </span>
          </label>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pt-2">
            <div className="text-xs text-neutral-500 flex items-center gap-2">
              <Upload className="w-3.5 h-3.5" />
              Approved creators receive a password-set link to complete setup.
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90 font-semibold px-6"
              data-testid="submit-apply-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Submit application
            </Button>
          </div>
        </form>

        <div className="mt-8 text-sm text-neutral-500">
          Selling subscriptions or memberships at company scale?&nbsp;
          <Link to="/partners/apply" className="text-[#00F0FF] hover:underline">
            Apply as a verified business partner &rarr;
          </Link>
        </div>
      </section>
    </div>
  );
}
